import React, { useState, useEffect } from 'react';
import PaymentReceiptTemplate from './PaymentReceiptTemplate';
import {
  migrateReceiptsFromLocalStorage,
  subscribeReceipts,
  saveReceipt,
  deleteReceipt,
} from '../../../services/firestoreReceipts';
import './PaymentReceiptsManager.css';

const PaymentReceiptsManager = () => {
  const [receipts, setReceipts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saveError, setSaveError] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [showReceiptTemplate, setShowReceiptTemplate] = useState(false);
  const [selectedReceipt, setSelectedReceipt] = useState(null);
  const [editingReceipt, setEditingReceipt] = useState(null);
  const [useCustomReceiptId, setUseCustomReceiptId] = useState(false);
  const [formData, setFormData] = useState({
    id: '',
    receiptNumber: '',
    customReceiptId: '',
    recipientName: '',
    recipientEmail: '',
    recipientPhone: '',
    recipientAddress: '',
    date: new Date().toISOString().split('T')[0],
    amount: 0,
    notes: '',
    status: 'draft',
  });

  useEffect(() => {
    let unsubscribe;

    async function init() {
      try {
        await migrateReceiptsFromLocalStorage();
        unsubscribe = subscribeReceipts((list) => {
          setReceipts(list);
          setLoading(false);
        });
      } catch (err) {
        console.error('Receipts sync init failed', err);
        setSaveError('Could not load receipts. Check connection.');
        setLoading(false);
      }
    }

    init();
    return () => {
      if (unsubscribe) unsubscribe();
    };
  }, []);

  const generateReceiptNumber = () => {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
    const random = Math.floor(Math.random() * 1000).toString().padStart(3, '0');
    return `RCP-${year}${month}${day}-${random}`;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaveError(null);
    try {
      const receiptNumber = useCustomReceiptId && formData.customReceiptId
        ? formData.customReceiptId
        : (formData.receiptNumber || generateReceiptNumber());
      const payload = {
        ...formData,
        id: editingReceipt ? editingReceipt.id : Date.now().toString(),
        receiptNumber,
        createdAt: editingReceipt ? editingReceipt.createdAt : new Date().toISOString(),
      };
      await saveReceipt(payload);
      resetForm();
      setShowModal(false);
    } catch (err) {
      console.error('Save receipt failed', err);
      setSaveError('Failed to save receipt. Try again.');
    }
  };

  const resetForm = () => {
    setFormData({
      id: '',
      receiptNumber: '',
      customReceiptId: '',
      recipientName: '',
      recipientEmail: '',
      recipientPhone: '',
      recipientAddress: '',
      date: new Date().toISOString().split('T')[0],
      amount: 0,
      notes: '',
      status: 'draft',
    });
    setEditingReceipt(null);
    setUseCustomReceiptId(false);
  };

  const handleEdit = (receipt, e) => {
    e.preventDefault();
    e.stopPropagation();
    setFormData(receipt);
    setEditingReceipt(receipt);
    const isCustomId = receipt.receiptNumber && !receipt.receiptNumber.startsWith('RCP-');
    setUseCustomReceiptId(isCustomId);
    if (isCustomId) {
      setFormData(prev => ({ ...prev, customReceiptId: receipt.receiptNumber }));
    }
    setShowModal(true);
  };

  const handleViewReceipt = (receipt, e) => {
    e.preventDefault();
    e.stopPropagation();
    setSelectedReceipt(receipt);
    setShowReceiptTemplate(true);
  };

  const handleDelete = async (id, e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!window.confirm('Are you sure you want to delete this receipt?')) return;
    setSaveError(null);
    try {
      await deleteReceipt(id);
    } catch (err) {
      console.error('Delete receipt failed', err);
      setSaveError('Failed to delete receipt. Try again.');
    }
  };

  return (
    <div className="receipts-manager">
      <div className="receipts-header">
        <h2>Payment Receipts Management</h2>
        <button onClick={() => setShowModal(true)} className="add-receipt-btn" disabled={loading}>
          + Create New Receipt
        </button>
      </div>

      {saveError && (
        <div className="receipts-error" role="alert">
          {saveError}
        </div>
      )}

      {loading ? (
        <div className="receipts-loading">Loading receipts…</div>
      ) : (
      <div className="receipts-grid">
        {receipts.map(receipt => (
          <div key={receipt.id} className="receipt-card">
            <div className="receipt-header">
              <h3>{receipt.receiptNumber}</h3>
              <span className={`status ${receipt.status}`}>{receipt.status}</span>
            </div>
            <div className="receipt-details">
              <p><strong>Recipient:</strong> {receipt.recipientName}</p>
              <p><strong>Date:</strong> {receipt.date}</p>
              <p><strong>Amount:</strong> ${parseFloat(receipt.amount || 0).toFixed(2)}</p>
            </div>
            <div className="receipt-actions">
              <button onClick={(e) => handleViewReceipt(receipt, e)} className="view-btn">View Receipt</button>
              <button onClick={(e) => handleEdit(receipt, e)} className="edit-btn">Edit</button>
              <button onClick={(e) => handleDelete(receipt.id, e)} className="delete-btn">Delete</button>
            </div>
          </div>
        ))}
      </div>
      )}

      {showModal && (
        <div className="modal-overlay">
          <div className="modal-content receipt-modal-content">
            <div className="modal-header">
              <h3>{editingReceipt ? 'Edit Receipt' : 'Create New Receipt'}</h3>
              <button onClick={() => { setShowModal(false); resetForm(); }} className="close-btn">×</button>
            </div>
            <form onSubmit={handleSubmit} className="receipt-form">
              {/* --- Recipient Info Section --- */}
              <div className="section-heading">
                <span>Recipient Info</span>
                <div className="section-divider"></div>
              </div>
              
              {/* Receipt ID Section */}
              <div className="invoice-id-section">
                <div 
                  className="invoice-id-toggle"
                  onClick={() => {
                    setUseCustomReceiptId(!useCustomReceiptId);
                    if (useCustomReceiptId) {
                      setFormData({ ...formData, customReceiptId: '' });
                    }
                  }}
                >
                  <div className={`toggle-switch ${useCustomReceiptId ? 'active' : ''}`}>
                    <div className="toggle-slider"></div>
                  </div>
                  <div>
                    <div className="toggle-label">Use Custom Receipt ID</div>
                    <div className="toggle-description">
                      {useCustomReceiptId ? 'Enter your custom receipt ID format' : 'Auto-generate receipt ID (RCP-YYYYMMDD-XXX)'}
                    </div>
                  </div>
                </div>
                
                {useCustomReceiptId && (
                  <div className="custom-invoice-input-wrapper">
                    <input
                      type="text"
                      value={formData.customReceiptId}
                      onChange={(e) => setFormData({ ...formData, customReceiptId: e.target.value })}
                      placeholder="Enter custom receipt ID (e.g., RCP-2024-001)"
                      required={useCustomReceiptId}
                      className="custom-invoice-input"
                    />
                    {formData.customReceiptId && (
                      <div className="invoice-id-preview">
                        {formData.customReceiptId}
                      </div>
                    )}
                  </div>
                )}
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Recipient Name</label>
                  <input
                    type="text"
                    value={formData.recipientName}
                    onChange={(e) => setFormData({ ...formData, recipientName: e.target.value })}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Recipient Email</label>
                  <input
                    type="email"
                    value={formData.recipientEmail}
                    onChange={(e) => setFormData({ ...formData, recipientEmail: e.target.value })}
                  />
                </div>
                <div className="form-group">
                  <label>Recipient Phone</label>
                  <input
                    type="tel"
                    value={formData.recipientPhone}
                    onChange={(e) => setFormData({ ...formData, recipientPhone: e.target.value })}
                    placeholder="+1 (555) 123-4567"
                  />
                </div>
                <div className="form-group">
                  <label>Date</label>
                  <input
                    type="date"
                    value={formData.date}
                    onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                    required
                  />
                </div>
              </div>
              
              <div className="form-row">
                <div className="form-group" style={{ flex: 1 }}>
                  <label>Recipient Address (Optional)</label>
                  <textarea
                    value={formData.recipientAddress}
                    onChange={(e) => setFormData({ ...formData, recipientAddress: e.target.value })}
                    placeholder="Street address, City, State, ZIP"
                    rows="2"
                  />
                </div>
              </div>

              {/* --- Payment Details Section --- */}
              <div className="section-heading">
                <span>Payment Details</span>
                <div className="section-divider"></div>
              </div>
              
              <div className="form-row">
                <div className="form-group">
                  <label>Amount Received ($)</label>
                  <input
                    type="number"
                    value={formData.amount}
                    onChange={(e) => setFormData({ ...formData, amount: parseFloat(e.target.value) || 0 })}
                    min="0"
                    step="0.01"
                    required
                  />
                </div>
              </div>

              {/* --- Summary Section --- */}
              <div className="section-heading">
                <span>Summary</span>
                <div className="section-divider"></div>
              </div>
              
              <div className="summary-card">
                <div className="summary-row total-row">
                  <span>Total Amount</span>
                  <span>${parseFloat(formData.amount || 0).toFixed(2)}</span>
                </div>
              </div>

              {/* --- Notes & Status --- */}
              <div className="section-heading">
                <span>Other</span>
                <div className="section-divider"></div>
              </div>
              <div className="form-row">
                <div className="form-group" style={{ flex: 2 }}>
                  <label>Notes</label>
                  <textarea
                    value={formData.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    rows="2"
                  />
                </div>
                <div className="form-group" style={{ flex: 1 }}>
                  <label>Status</label>
                  <select
                    value={formData.status}
                    onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                  >
                    <option value="draft">Draft</option>
                    <option value="sent">Sent</option>
                    <option value="paid">Paid</option>
                    <option value="cancelled">Cancelled</option>
                  </select>
                </div>
              </div>

              {/* --- Actions --- */}
              <div className="form-actions">
                <button type="button" onClick={() => { setShowModal(false); resetForm(); }} className="cancel-btn">
                  Cancel
                </button>
                <button type="submit" className="save-btn">
                  {editingReceipt ? 'Update Receipt' : 'Create Receipt'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showReceiptTemplate && selectedReceipt && (
        <PaymentReceiptTemplate 
          receipt={selectedReceipt}
          onClose={() => {
            setShowReceiptTemplate(false);
            setSelectedReceipt(null);
          }}
        />
      )}
    </div>
  );
};

export default PaymentReceiptsManager;

